import Chart from 'chart.js/auto';
import { startMockFrequencyStream, stopMockFrequencyStream } from './mock-frequency-stream';
import { getTimeZoneLabel } from '../../tools/helpers';

export const CLASSNAME_CANVAS = '.org--net-frequency__graph__canvas';
export const MAX_VISIBLE_POINTS = 10;

// Mock oder data-stream-url aus index.hbs
export const USE_MOCK_DATA = false;

// Mock-Intervall in ms
export const MOCK_INTERVAL = 2000;

// Zeitraum historischer Daten in Minuten - default: 10
export const STREAM_LOOKBACK = 10;

// Zeitintervall zwischen Messungen in Sekunden - default 10
export const STREAM_INTERVAL = 2;

// Anzahl der Nachkommastellen des Messwerts - default: 3
export const STREAM_DECIMALS = 3;

// ts: Unix-Timestamp -  default: HH:mm:ss (ohne Parameter)
export const STREAM_TIME_FORMAT = 'ts';

// Y-Achse Minimum
export const Y_AXIS_MIN = 49.800;

// Y-Achse Maximum
export const Y_AXIS_MAX = 50.200;

// Chart-Farben
export const CHART_COLOR_LINE = 'rgba(0, 169, 224, 0.5)';
export const CHART_COLOR_AREA_START = 'rgba(0, 169, 224, 0.35)';
export const CHART_COLOR_AREA_END = 'rgba(0, 169, 224, 0)';
export const CHART_COLOR_AREA_FALLBACK = 'rgba(0, 169, 224, 0.18)';
export const CHART_COLOR_CURRENT_POINT = '#323D48';
export const CHART_COLOR_CURRENT_POINT_BORDER = '#ffffff';
export const CHART_COLOR_POINT = '#2268B1';
export const CHART_COLOR_GUIDE_LINE = '#323D48';
export const CHART_COLOR_CENTER_GRID_LINE = '#d0d0d0';
export const CHART_COLOR_AXIS_LABEL = '#323D48';

// Labels
const LABELS = {
  de: {
    chartTitle: 'Netzfrequenz',
    live: 'LIVE',
    testData: 'TESTDATEN',
    paused: 'Pausiert',
    offline: 'Offline',
    errorTitle: 'Ein Fehler ist aufgetreten.',
    errorText: 'Aktuell können keine Live-Daten empfangen werden.\n Entschuldigen Sie bitte.',
    pause: 'Pausieren',
    resume: 'Fortsetzen',
    datasetLabel: 'Netzfrequenz',
    time: 'Zeit',
    frequency: 'Frequenz',
  },
  en: {
    chartTitle: 'Grid frequency',
    live: 'LIVE',
    testData: 'TEST DATA',
    paused: 'Paused',
    offline: 'Offline',
    errorTitle: 'An error has occurred.',
    errorText: 'Live data cannot currently be received. We apologize.',
    pause: 'Pause',
    resume: 'Resume',
    datasetLabel: 'Grid frequency',
    time: 'Time',
    frequency: 'Frequency',
  },
};

class ChartNetFrequency {
  constructor(options = {}) {
    this.element = options.element;
    this.canvas = this.element.querySelector(CLASSNAME_CANVAS);
    this.baseUrl = this.element.dataset.streamUrl;
    this.streamInterval = Number(this.element.dataset.streamInterval) || STREAM_INTERVAL;
    this.language = document.documentElement.lang === 'en' ? 'en' : 'de';
    this.chart = null;
    this.eventSource = null;
    this.mockTimer = null;
    this.isPaused = false;
    this.titleElement = this.element.querySelector('.org--net-frequency__graph__title');
    this.timeValueElement = this.element.querySelector('.org--net-frequency__graph__time-value');
    this.timeZoneElement = this.element.querySelector('.org--net-frequency__graph__time-zone');
    this.deltaElement = this.element.querySelector('.org--net-frequency__graph__delta');
    this.deltaValueElement = this.element.querySelector('.org--net-frequency__graph__delta-value');
    this.frequencyValueElement = this.element.querySelector('.org--net-frequency__graph__frequency-value');
    this.frequencyUnitElement = this.element.querySelector('.org--net-frequency__graph__frequency-unit');
    this.liveElement = this.element.querySelector('.org--net-frequency__graph__live');
    this.liveTextElement = this.element.querySelector('.org--net-frequency__graph__live-text');
    this.actionButtonElement = this.element.querySelector('.org--net-frequency__graph__action-button');
    this.errorTitleElement = this.element.querySelector('.org--net-frequency__graph__error-title');
    this.errorTextElement = this.element.querySelector('.org--net-frequency__graph__error-text');
    this.dateElement = this.element.querySelector('.org--net-frequency__graph__date-value');
    this.retryElement = this.element.querySelector('.org--net-frequency__graph__retry');
  }

  init() {
    if (!this.canvas) {
      return;
    }

    if (this.liveTextElement) {
      this.liveTextElement.textContent = this.getLiveText();
    }

    if (this.titleElement) {
      this.titleElement.textContent = this.getLabel('chartTitle');
    }

    this.renderChart();
    this.bindActionButton();
    this.startStream();
    this.bindRetry();

    // TEST: Fehlerzustand simulieren
    // this.showErrorState();
  }

  cleanupStream() {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }

    if (this.mockTimer) {
      stopMockFrequencyStream(this.mockTimer);
      this.mockTimer = null;
    }
  }

  resetChart() {
    if (!this.chart) {
      return;
    }

    this.chart.data.labels = [];
    this.chart.data.datasets[0].data = [];
    this.chart.data.datasets[0].timestamps = [];
    this.chart.update();
  }

  bindRetry() {
    if (!this.retryElement) {
      return;
    }

    this.retryElement.addEventListener('click', (event) => {
      event.preventDefault();
      this.reloadComponent();
    });
  }

  reloadComponent(options = {}) {
    const { immediate = false } = options;

    this.element.classList.remove('is-error');

    this.cleanupStream();
    this.resetChart();

    if (immediate) {
      this.startStream();
      return;
    }

    window.setTimeout(() => {
      this.startStream();
    }, 1000);
  }

  getLabel(key) {
    return LABELS[this.language][key];
  }

  getLiveText() {
    return USE_MOCK_DATA ? this.getLabel('testData') : this.getLabel('live');
  }

  formatFrequency(value) {
    return Number(value).toFixed(3).replace('.', ',');
  }

  formatTime(timestamp) {
    return new Date(Number(timestamp)).toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'Europe/Berlin',
    });
  }

  formatDate(timestamp) {
    return new Date(Number(timestamp)).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: 'Europe/Berlin',
    });
  }

  getStreamUrl(options = {}) {
    const baseUrl = this.baseUrl;

    if (!baseUrl) {
      return '';
    }

    const params = new URLSearchParams();
    const lookback = options.lookback != null ? options.lookback : STREAM_LOOKBACK;

    if (lookback !== '' && lookback != null) {
      params.append('lookback', lookback);
    }

    if (this.streamInterval !== '' && this.streamInterval != null) {
      params.append('interval', this.streamInterval);
    }

    if (STREAM_DECIMALS !== '' && STREAM_DECIMALS != null) {
      params.append('decimals', STREAM_DECIMALS);
    }

    if (STREAM_TIME_FORMAT !== '' && STREAM_TIME_FORMAT != null) {
      params.append('timeFormat', STREAM_TIME_FORMAT);
    }

    return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
  }

  startStream(options = {}) {
    this.cleanupStream();

    if (USE_MOCK_DATA) {
      this.mockTimer = startMockFrequencyStream((item) => {
        this.handleFrequencyData(item);
      }, MOCK_INTERVAL);

      return;
    }

    this.eventSource = new window.EventSource(this.getStreamUrl(options));

    this.eventSource.onmessage = (event) => {
      let payload;

      try {
        payload = JSON.parse(event.data);
      } catch (error) {
        return;
      }

      if (Array.isArray(payload)) {
        payload.forEach(item => this.handleFrequencyData(item));
        return;
      }

      this.handleFrequencyData(payload);
    };

    this.eventSource.onerror = () => {
      this.cleanupStream();
      this.showErrorState();
    };
  }

  bindActionButton() {
    if (!this.actionButtonElement) {
      return;
    }

    this.actionButtonElement.addEventListener('click', () => {
      this.toggleStream();
    });

    this.actionButtonElement.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }

      event.preventDefault();
      this.toggleStream();
    });
  }

  pauseStream() {
    this.isPaused = true;

    this.cleanupStream();

    this.updateActionButtonState();
  }

  resumeStream() {
    this.isPaused = false;

    this.updatePausedHoverState(false);
    this.updateActionButtonState();

    this.reloadComponent({ immediate: true });
  }


  toggleStream() {
    if (this.isPaused) {
      this.resumeStream();
      return;
    }

    this.pauseStream();
  }

  updateActionButtonState() {
    if (!this.actionButtonElement) {
      return;
    }

    this.actionButtonElement.classList.toggle('is-paused', this.isPaused);

    this.actionButtonElement.setAttribute(
      'aria-label',
      this.isPaused ? this.getLabel('resume') : this.getLabel('pause'),
    );

    if (this.liveElement) {
      this.liveElement.classList.toggle('is-paused', this.isPaused);
    }

    if (this.liveTextElement) {
      this.liveTextElement.textContent = this.isPaused ? this.getLabel('paused') : this.getLiveText();
    }
  }

  showErrorState() {
    this.element.classList.add('is-error');

    if (this.liveTextElement) {
      this.liveTextElement.textContent = this.getLabel('offline');
    }

    if (this.errorTitleElement) {
      this.errorTitleElement.textContent = this.getLabel('errorTitle');
    }

    if (this.errorTextElement) {
      this.errorTextElement.textContent = this.getLabel('errorText');
    }
  }

  // Im pausierten Zustand Werte beim Hover über einen Punkt einfärben
  updatePausedHoverState(isHoveringPoint) {
    const hoverColor = isHoveringPoint && this.isPaused ? CHART_COLOR_POINT : '';

    if (this.timeValueElement) {
      this.timeValueElement.style.color = hoverColor;
    }

    if (this.timeZoneElement) {
      this.timeZoneElement.style.color = hoverColor;
    }

    if (this.frequencyValueElement) {
      this.frequencyValueElement.style.color = hoverColor;
    }

    if (this.frequencyUnitElement) {
      this.frequencyUnitElement.style.color = hoverColor;
    }

    if (this.deltaElement) {
      this.deltaElement.style.color = hoverColor;
    }
  }

  // Zeitwert HH:mm:ss in Sekunden umrechnen
  getSecondsFromTime(time) {
    const timeParts = String(time).split(':').map(Number);

    if (timeParts.length !== 3 || timeParts.some(Number.isNaN)) {
      return null;
    }

    const hours = timeParts[0];
    const minutes = timeParts[1];
    const seconds = timeParts[2];

    return (hours * 60 * 60) + (minutes * 60) + seconds;
  }

  // Zeitdifferenz zwischen aktuellem Punkt und Hover-Punkt formatieren
  getTimeDifferenceLabel(hoveredTime) {
    const currentIndex = this.chart.data.labels.length - 1;
    const currentTime = this.chart.data.labels[currentIndex];

    const currentSeconds = this.getSecondsFromTime(currentTime);
    const hoveredSeconds = this.getSecondsFromTime(hoveredTime);

    if (currentSeconds === null || hoveredSeconds === null) {
      return '';
    }

    let diffSeconds = hoveredSeconds - currentSeconds;

    // Falls die Daten über Mitternacht laufen
    if (diffSeconds > 12 * 60 * 60) {
      diffSeconds -= 24 * 60 * 60;
    }

    if (diffSeconds < -12 * 60 * 60) {
      diffSeconds += 24 * 60 * 60;
    }

    const sign = diffSeconds > 0 ? '+' : '';

    return ` (${sign}${diffSeconds} Sek)`;
  }

  // Im pausierten Zustand Werte des gehoverten Punktes anzeigen
  updatePausedHoverValues(chartElements) {
    if (!this.isPaused || !chartElements.length || !this.chart) {
      return;
    }

    const hoveredPoint = chartElements[0];
    const hoveredIndex = hoveredPoint.index;
    const hoveredTime = this.chart.data.labels[hoveredIndex];
    const hoveredFrequency = this.chart.data.datasets[0].data[hoveredIndex];

    if (this.timeValueElement) {
      // Im pausierten Zustand Zeitdifferenz zum aktuellen Punkt anzeigen
      this.timeValueElement.innerHTML = `${hoveredTime}<span class="org--net-frequency__graph__time-difference">${this.getTimeDifferenceLabel(hoveredTime)}</span>`;
    }

    if (this.frequencyValueElement) {
      this.frequencyValueElement.textContent = this.formatFrequency(hoveredFrequency);
    }

    if (this.deltaValueElement) {
      const delta = hoveredFrequency - 50;
      const sign = delta >= 0 ? '+' : '';

      this.deltaValueElement.textContent = `${sign}${this.formatFrequency(delta)}`;
    }
  }

  handleFrequencyData(item) {
    if (!item || item.status !== 'ok') {
      return;
    }

    const frequency = parseFloat(String(item.frequency).replace(',', '.'));

    if (Number.isNaN(frequency)) {
      return;
    }

    const time = this.formatTime(item.timestamp);

    this.element.classList.remove('is-error');
    this.chart.data.labels.push(time);
    this.chart.data.datasets[0].timestamps.push(item.timestamp);
    this.chart.data.datasets[0].data.push(frequency);

    if (this.timeValueElement) {
      this.timeValueElement.textContent = time;
    }

    if (this.dateElement) {
      this.dateElement.textContent = this.formatDate(item.timestamp);
    }

    if (this.timeZoneElement) {
      // getTimeZoneLabel bekommt weiterhin den Unix-Timestamp
      this.timeZoneElement.textContent = getTimeZoneLabel(item.timestamp, this.language);
    }

    if (this.frequencyValueElement) {
      this.frequencyValueElement.textContent = this.formatFrequency(frequency);
    }

    if (this.deltaValueElement) {
      const delta = frequency - 50;
      const sign = delta >= 0 ? '+' : '';

      this.deltaValueElement.textContent = `${sign}${this.formatFrequency(delta)}`;
    }

    if (this.chart.data.labels.length > MAX_VISIBLE_POINTS) {
      this.chart.data.labels.shift();
      this.chart.data.datasets[0].data.shift();
      this.chart.data.datasets[0].timestamps.shift();
    }

    this.chart.update();
  }

  renderChart() {
    const ctx = this.canvas.getContext('2d');
    const isLastPoint = context => context.dataIndex === context.dataset.data.length - 1;

    const currentPointGuideLinePlugin = {
      id: 'currentPointGuideLinePlugin',
      afterDatasetsDraw(chart) {
        const datasetMeta = chart.getDatasetMeta(0);
        const lastPoint = datasetMeta.data[datasetMeta.data.length - 1];

        if (!lastPoint) {
          return;
        }

        // eslint-disable-next-line no-shadow
        const { ctx, chartArea } = chart;
        const lineTop = chartArea.top;
        const lineBottom = chartArea.bottom;

        ctx.save();
        ctx.beginPath();
        ctx.lineWidth = 2;
        ctx.strokeStyle = CHART_COLOR_GUIDE_LINE;
        ctx.moveTo(lastPoint.x, lineBottom);
        ctx.lineTo(lastPoint.x, lineTop);
        ctx.stroke();
        ctx.restore();
      },
    };

    this.chart = new Chart(ctx, {
      plugins: [currentPointGuideLinePlugin],
      type: 'line',
      data: {
        labels: [],
        datasets: [
          {
            label: this.getLabel('datasetLabel'),
            data: [],
            timestamps: [],
            borderWidth: 4,
            borderColor: CHART_COLOR_LINE,
            backgroundColor(context) {
              const chart = context.chart;
              // eslint-disable-next-line no-shadow
              const ctx = chart.ctx;
              const chartArea = chart.chartArea;

              if (!chartArea) {
                return CHART_COLOR_AREA_FALLBACK;
              }

              const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
              gradient.addColorStop(0, CHART_COLOR_AREA_START);
              gradient.addColorStop(1, CHART_COLOR_AREA_END);

              return gradient;
            },
            pointBackgroundColor(context) {
              return isLastPoint(context) ? CHART_COLOR_CURRENT_POINT : CHART_COLOR_POINT;
            },
            pointBorderColor(context) {
              return isLastPoint(context) ? CHART_COLOR_CURRENT_POINT_BORDER : CHART_COLOR_POINT;
            },
            pointRadius(context) {
              return isLastPoint(context) ? 9 : 3;
            },
            pointHoverRadius() {
              return 9;
            },
            hitRadius: 12,
            tension: 0.35,
            fill: true,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        onHover: (event, chartElements) => {
          const isHoveringPoint = chartElements.length > 0;

          // eslint-disable-next-line no-param-reassign
          event.native.target.style.cursor = isHoveringPoint ? 'pointer' : 'default';

          // Im pausierten Zustand Werte beim Hover blau einfärben
          this.updatePausedHoverState(isHoveringPoint);

          // Im pausierten Zustand Werte des gehoverten Punktes anzeigen
          this.updatePausedHoverValues(chartElements);
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            // Texte im Overlay/Tooltip inkl. Zeitzone
            callbacks: {
              title: (tooltipItems) => {
                const item = tooltipItems[0];

                const timestamp = this.chart.data.datasets[0].timestamps[item.dataIndex];
                return `${item.label} ${getTimeZoneLabel(timestamp, this.language)}`;
              },
              label: context => `${this.formatFrequency(context.parsed.y)} Hz`,
            },
            titleFont: () => ({
              family: 'Gotham, Helvetica, Arial, sans-serif;',
            }),
            bodyFont: () => ({
              family: 'Gotham, Helvetica, Arial, sans-serif;',
            }),
            footerFont: () => ({
              family: 'Gotham, Helvetica, Arial, sans-serif;',
            }),
            displayColors: false,
          },
        },
        scales: {
          x: {
            grid: {
              display: false,
              drawTicks: false,
            },
            border: {
              display: false,
            },
            ticks: {
              autoSkip: false,
              maxRotation: 0,
              minRotation: 0,
              callback() {
                return '';
              },
            },
          },
          y: {
            min: Y_AXIS_MIN,
            max: Y_AXIS_MAX,
            afterFit(scale) {
              // eslint-disable-next-line no-param-reassign
              scale.width = Math.max(scale.width, 58);
            },
            afterBuildTicks(axis) {
              // eslint-disable-next-line no-param-reassign
              axis.ticks = [
                { value: Y_AXIS_MIN },
                { value: 50 },
                { value: Y_AXIS_MAX },
              ];
            },
            ticks: {
              color: CHART_COLOR_AXIS_LABEL,
              font: {
                family: 'Gotham, Helvetica, Arial, sans-serif',
                weight: '500',
              },
              callback: (value) => {
                if (value === Y_AXIS_MIN || value === 50 || value === Y_AXIS_MAX) {
                  return this.formatFrequency(value);
                }

                return '';
              },
            },
            grid: {
              color(context) {
                if (context.tick && context.tick.value === 50) {
                  return CHART_COLOR_CENTER_GRID_LINE;
                }

                return 'transparent';
              },
            },
          },
        },
      },
    });
  }
}

export default ChartNetFrequency;
